import { Input, Spin } from 'antd'
import { useEffect, useRef, useState, useMemo } from 'react'
import { SDrawerOrModal, SGeneralForms, SListTabs } from 'shj-components'
import FeeTemplateConfigTable from '../components/feeTemplateConfigTable'
import { getCostTemplateDetailCategoryType, getProductEnums } from '@/services/product'
import debounce from 'lodash/debounce'
import { SearchOutlined } from '@ant-design/icons'

const AddConfigDrawer = (props: {
  open: boolean
  budgetList: any
  listRef: any
  onCancel: () => void
}) => {
  const { open, budgetList, listRef, onCancel } = props

  const formRef = useRef<any>()
  const [categoryTypeList, setCategoryTypeList] = useState([])
  const [quoteType, setQuoteType] = useState('')
  const [categoryType, setCategoryType] = useState('')
  const [itemName, setItemName] = useState('')
  const [loading, setLoading] = useState(false)
  const [rawTabList, setRawTabList] = useState<any[]>([])

  const chooseItem = useMemo(
    () => ({
      quoteType,
      categoryType,
      itemName,
    }),
    [quoteType, categoryType, itemName]
  )

  // 获取费用模版详情所属分类类型
  const getCategoryType = async () => {
    const res: any = await getCostTemplateDetailCategoryType()
    res.map((item: any) => {
      item.label = item.name
      item.value = item.code
    })
    setCategoryTypeList(res)
    setCategoryType(res[0].value)
  }

  // 使用 useMemo 动态创建 tabList
  const tabList = useMemo(() => {
    if (!rawTabList.length) return []

    return rawTabList.map((item: any) => ({
      ...item,
      label: item.description,
      destroyInactiveTabPane: true,
      children: (
        <FeeTemplateConfigTable
          chooseItem={chooseItem}
          activeTabKey={item.key}
          outsideListRef={listRef}
        />
      ),
    }))
  }, [rawTabList, chooseItem, listRef])

  // 获取费用项类型
  const getTabList = async () => {
    const data = await getProductEnums('com.shj.saas.plus.product.enums.CostTypeEnum')
    setRawTabList(data) // 存储原始数据
  }

  // 创建一个防抖函数的引用，确保它在组件生命周期内保持一致
  const debouncedSetItemNameRef = useRef(
    debounce((value: string) => {
      setItemName(value)
    }, 300)
  ).current

  // 初始化
  useEffect(() => {
    if (open) {
      setLoading(true)
      setQuoteType(budgetList[0].code)
      Promise.all([getTabList(), getCategoryType()]).finally(() => {
        setLoading(false)
      })
    } else {
      setItemName('')
      setCategoryType('')
      setQuoteType('')
    }
  }, [open])

  // 组件卸载时取消防抖
  useEffect(() => {
    return () => {
      debouncedSetItemNameRef.cancel()
    }
  }, [debouncedSetItemNameRef])

  return (
    <SDrawerOrModal
      title={'新增模板配置'}
      open={open}
      onCancel={onCancel}
      width={800}
      antdProps={{ footer: false }}
      destroyOnClose={true}
    >
      {!loading ? (
        <div style={{ margin: '-10px 0px'}}>
          <SGeneralForms
            ref={formRef}
            layout={'vertical'}
            configs={[
              {
                formItem: [
                  {
                    label: '报价类型',
                    name: 'quoteType',
                    spanCol: 16,
                    required: true,
                    type: 'select',
                    rules: [{ required: true, message: '报价类型不能为空' }],
                    comConfig: {
                      fieldNames: { label: 'name', value: 'code' },
                      options: budgetList,
                      onChange: (value: any) => setQuoteType(value),
                      defaultValue: quoteType,
                    },
                  },
                  {
                    label: '所属类目',
                    name: 'categoryType',
                    spanCol: 16,
                    required: true,
                    type: 'radio',
                    rules: [{ required: true, message: '所属类目不能为空' }],
                    comConfig: {
                      options: categoryTypeList,
                      onChange: (e: any) => setCategoryType(e.target.value),
                      defaultValue: categoryType,
                    },
                  },
                  {
                    name: 'itemName',
                    spanCol: 16,
                    children: (
                      <Input
                        placeholder="搜索费用项名称"
                        onChange={e => debouncedSetItemNameRef(e.target.value)}
                        addonAfter={<SearchOutlined />}
                        style={{ marginTop: 20 }}
                        allowClear
                      />
                    ),
                  },
                ],
              },
            ]}
          />
          <SListTabs
            defaultActiveKey={'1'}
            items={tabList}
            hideExtra={false}
            auth={false}
            style={{ marginTop: -10 }}
          />
        </div>
      ) : (
        <div style={{ margin: '0 10px' }}>
          <Spin tip="Loading..." />
        </div>
      )}
    </SDrawerOrModal>
  )
}

export default AddConfigDrawer
